<template>
  <div
    class="owner-program-detail bg-u-px-md"
    data-testid="owner-program-detail"
  >
    <bg-breadcrumb :links="breadcrumbLinks" tag="router-link" />

    <bg-tabs v-model="activeTabIndex" class="bg-u-mt-md">
      <bg-tab id="program" title="Program">
        <div
          v-if="isLoading"
          class="owner-program-detail__loading"
          data-testid="detail-loading"
        >
          <bg-text size="body-2" class="bg-u-text-neutral-500">
            Memuat detail program...
          </bg-text>
        </div>

        <owner-program-form
          v-else-if="formState"
          ref="formRef"
          mode="edit"
          :initial-data="formState"
          :status="programStatus"
          :program-id="programId"
          class="bg-u-mt-md"
          @end-program="openEndProgramModal"
        />
      </bg-tab>

      <bg-tab id="result" title="Result" :disabled="!isResultEnabled">
        <owner-program-result
          v-if="activeTabIndex === RESULT_TAB_INDEX && resultProps"
          :program-id="programId"
          :target-type="resultProps.targetType"
          :cta-type="resultProps.ctaType"
          :ctas="resultProps.ctas"
          class="bg-u-mt-md"
        />
      </bg-tab>
    </bg-tabs>

    <bg-modal
      :value="isEndProgramModalOpen"
      title="Yakin ingin akhiri program ini sekarang?"
      button-main-text="Akhiri Program"
      button-second-text="Batal"
      :button-main-props="{ loading: isEndingProgram }"
      @close="closeEndProgramModal"
      @click-main-action="endProgram"
      @click-second-action="closeEndProgramModal"
    >
      <bg-text size="body-2">
        Dengan akhiri program, status akan berubah menjadi
        <b>Berakhir</b> dan program tidak akan tampil lagi di Dashboard
        Singgahsini pemilik.
      </bg-text>
    </bg-modal>

    <bg-modal
      :value="isPromptOpen"
      data-testid="leave-confirmation-modal"
      title="Yakin keluar tanpa menyimpan?"
      :button-main-text="leavePromptActions.mainText"
      :button-second-text="leavePromptActions.secondText"
      :button-main-props="{ loading: isProcessing }"
      :body-scroll="false"
      @input="handlePromptInput"
      @click-main-action="leavePromptActions.mainAction()"
      @click-second-action="leavePromptActions.secondAction()"
    >
      <bg-text v-if="isDraft" size="body-2">
        Simpan perubahan sebagai draft sebelum meninggalkan halaman ini.
      </bg-text>

      <bg-text v-else size="body-2" v-html="leavePromptDescription" />
    </bg-modal>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
  nextTick,
  watch,
} from '@vue/composition-api';
import { BgBreadcrumb, BgTabs, BgTab, BgText, BgModal } from 'bangul-vue';

import { STATUS_LABEL } from '@admin/pages/OwnerProgram/constants';

import ownerProgramApi from '@admin_endpoints/owner-program';
import OwnerProgramForm from '@admin/components/pages/OwnerProgram/OwnerProgramForm/OwnerProgramForm.vue';
import OwnerProgramResult from '@admin/components/pages/OwnerProgram/OwnerProgramResult';
import { mapProgramDetailToForm } from '@admin/components/pages/OwnerProgram/OwnerProgramForm/utils/mapProgramDetailToForm';
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard';

const RESULT_TAB_INDEX = 1;

export default {
  name: 'OwnerProgramDetail',

  components: {
    BgBreadcrumb,
    BgTabs,
    BgTab,
    BgText,
    BgModal,
    OwnerProgramForm,
    OwnerProgramResult,
  },

  beforeRouteLeave(to, from, next) {
    this.guardLeave(next);
  },

  setup() {
    const { $route, $router, $toast, $error } = getCurrentInstance().proxy;

    const activeTabIndex = ref(0);

    const breadcrumbLinks = [
      {
        name: 'Program Pemilik',
        url: '/owner-program',
      },
      {
        name: 'Detail Program',
        url: '',
      },
    ];

    const programId = $route.params.id;

    const formRef = ref(null);
    const isLoading = ref(true);
    const formState = ref(null);
    const programStatus = ref(null);

    // Suppresses the leave guard for navigation that follows a completed action
    // (e.g. ending the program) — that redirect is not an "unsaved exit".
    const bypassLeaveGuard = ref(false);

    const isDraft = computed(() => programStatus.value === 'draft');

    // Result only exists once a program has run; draft/scheduled have none yet.
    const isResultEnabled = computed(() =>
      ['active', 'ended'].includes(programStatus.value)
    );

    const resultProps = computed(() => {
      if (!formState.value) return null;

      const { buttonSettings, programTarget } = formState.value;
      const count = buttonSettings.ctaType === 'double' ? 2 : 1;

      return {
        targetType: programTarget.targetType,
        ctaType: buttonSettings.ctaType,
        ctas: buttonSettings.buttons.slice(0, count).map(button => ({
          action: button.action,
          label: button.label,
          fieldTitles: (button.textFields || []).map(field => field.title),
        })),
      };
    });

    const fetchProgramDetail = async () => {
      isLoading.value = true;

      try {
        const { data } = await ownerProgramApi.getOwnerProgram(programId);

        programStatus.value = data.program_owner.status;
        formState.value = mapProgramDetailToForm(data.program_owner);

        if ($route.query.tab === 'result' && isResultEnabled.value) {
          await nextTick();
          activeTabIndex.value = RESULT_TAB_INDEX;
        }
      } catch (error) {
        $error?.report?.(error);
        $toast.show('Gagal memuat detail program. Silakan coba lagi.', {
          variant: 'error',
        });
        $router.push({ name: 'owner-program' });
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(fetchProgramDetail);

    const hasUnsavedChanges = () =>
      !bypassLeaveGuard.value &&
      !!(formRef.value && formRef.value.hasUnsavedChanges);

    const {
      isPromptOpen,
      isProcessing,
      guardLeave,
      confirmLeave,
      cancelLeave,
      handlePromptInput,
    } = useUnsavedChangesGuard({
      hasUnsavedChanges,
      onConfirm: () => (isDraft.value ? formRef.value.saveAsDraft() : true),
    });

    const leavePromptDescription = computed(() => {
      const label = STATUS_LABEL[programStatus.value] || programStatus.value;

      return `Perubahan pada program yang sedang <b>${label}</b> belum disimpan. Jika keluar sekarang, perubahan pada program ini tidak akan tersimpan.`;
    });

    const leavePromptActions = computed(() => {
      if (isDraft.value) {
        return {
          mainText: 'Simpan Draft',
          secondText: 'Lanjut Edit',
          mainAction: () => confirmLeave(),
          secondAction: () => cancelLeave(),
        };
      }

      return {
        mainText: 'Lanjut Ubah Program',
        secondText: 'Keluar',
        mainAction: () => cancelLeave(),
        secondAction: () => confirmLeave(),
      };
    });

    const isEndProgramModalOpen = ref(false);
    const isEndingProgram = ref(false);

    const openEndProgramModal = () => {
      isEndProgramModalOpen.value = true;
    };

    const closeEndProgramModal = () => {
      isEndProgramModalOpen.value = false;
    };

    const endProgram = async () => {
      if (isEndingProgram.value) return;

      isEndingProgram.value = true;

      try {
        await ownerProgramApi.stopOwnerProgram(programId);

        closeEndProgramModal();
        $toast.show('Program berhasil diakhiri.');

        // Completed action — leave without the unsaved-changes prompt.
        bypassLeaveGuard.value = true;
        $router.push({ name: 'owner-program' });
      } catch (error) {
        $error?.report?.(error);
        $toast.show('Gagal mengakhiri program. Silakan coba lagi.', {
          variant: 'error',
        });
      } finally {
        isEndingProgram.value = false;
      }
    };

    watch(
      () => activeTabIndex.value,
      newIndex => {
        bypassLeaveGuard.value = newIndex === RESULT_TAB_INDEX;
      },
      { immediate: true }
    );

    return {
      RESULT_TAB_INDEX,
      activeTabIndex,
      breadcrumbLinks,
      isLoading,
      formState,
      formRef,
      programId,
      programStatus,
      isDraft,
      isResultEnabled,
      resultProps,
      leavePromptDescription,
      leavePromptActions,
      isPromptOpen,
      isProcessing,
      guardLeave,
      confirmLeave,
      cancelLeave,
      handlePromptInput,
      isEndProgramModalOpen,
      isEndingProgram,
      openEndProgramModal,
      closeEndProgramModal,
      endProgram,
    };
  },
};
</script>
